import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { getSelf } from "@/api/auth";
import { useSubscription } from "@/hooks/useSubscription";

interface NavItem {
  path: string;
  label: string;
  icon: string;
  activeIcon: string;
}

const navItems: NavItem[] = [
  { path: "/", label: "AI 看盘", icon: "ri-dashboard-2-line", activeIcon: "ri-dashboard-2-fill" },
  { path: "/research", label: "研报", icon: "ri-file-list-3-line", activeIcon: "ri-file-list-3-fill" },
  { path: "/ai-trader", label: "AI助手", icon: "ri-robot-2-line", activeIcon: "ri-robot-2-fill" },
  { path: "/ai-strategy", label: "AI策略", icon: "ri-function-line", activeIcon: "ri-function-fill" },
  { path: "/profile", label: "我的", icon: "ri-user-3-line", activeIcon: "ri-user-3-fill" },
];

export default function DesktopSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const sub = useSubscription();
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    getSelf().then(setUser).catch(() => {});
  }, []);

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <aside className="hidden lg:flex lg:flex-col lg:w-[220px] lg:min-h-screen lg:shrink-0 bg-white border-r border-background-200/60">
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 pt-6 pb-8">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
          <i className="ri-vip-crown-fill text-white text-lg" />
        </div>
        <div>
          <div className="text-base font-bold text-foreground-950 tracking-tight">天鼎AI</div>
          <div className="text-[10px] text-foreground-400">AI 投资助手</div>
        </div>
      </div>

      {/* Nav items */}
      <nav className="flex-1 px-3 space-y-1">
        {navItems.map((item) => {
          const active = isActive(item.path);
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer ${
                active ? "bg-primary-50" : "hover:bg-background-50"
              }`}
            >
              <i className={`${active ? item.activeIcon : item.icon} text-lg ${
                active ? "text-primary-500" : "text-foreground-400"
              }`} />
              <span className={`${active ? "text-foreground-950 font-semibold" : "text-foreground-600"}`}>
                {item.label}
              </span>
              {active && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary-500" />}
            </button>
          );
        })}
      </nav>

      {/* Bottom section — user info */}
      <div className="px-4 pb-6">
        <div className="h-px bg-background-100 mb-3" />
        <button onClick={() => navigate("/profile")}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-background-50 transition-colors cursor-pointer">
          <div className="w-7 h-7 rounded-full bg-primary-50 flex items-center justify-center shrink-0">
            <i className="ri-user-3-line text-primary-500 text-xs" />
          </div>
          <div className="flex-1 text-left min-w-0">
            <div className="text-xs font-medium text-foreground-700 truncate">
              {user?.displayName || user?.username || "用户"}
            </div>
            <div className="flex items-center gap-1 mt-0.5">
              {sub.subscribed ? (
                <>
                  <span className="text-[9px] px-1 py-0.5 rounded bg-amber-50 text-amber-600 font-medium">天鼎AI</span>
                  {sub.remainingDays !== undefined && (
                    <span className="text-[9px] text-foreground-400">{sub.remainingDays}天</span>
                  )}
                </>
              ) : (
                <span className="text-[9px] text-foreground-400">普通用户</span>
              )}
            </div>
          </div>
          <i className="ri-arrow-right-s-line text-foreground-300 text-sm" />
        </button>
      </div>
    </aside>
  );
}
